import React from 'react';
import { PreviewVariant } from '../../types/demo-previews';
import { MediaVariant } from '../../hooks/banners/use-template-utils';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';

interface PreviewRendererProps {
  previews: PreviewVariant | PreviewVariant[] | MediaVariant | string | undefined;
  thumbnail?: string;
}

const isMediaVariant = (value: unknown): value is MediaVariant =>
  !!value && typeof value === 'object' && !Array.isArray(value) && ('video' in value || 'image' in value);

const isImageUrl = (url: string) => /\.(gif|png|jpe?g|webp)$/i.test(url);

const imgStyle = (maxWidth: number): React.CSSProperties => ({
  width: '100%',
  maxWidth,
  height: 'auto',
  borderRadius: 8
});

/**
 * Renders a media URL as <img> or <video> based on file extension.
 * GIFs and static images render as <img>, everything else as <video>.
 */
const MediaElement: React.FC<{ src: string; maxWidth: number; style?: React.CSSProperties }> = ({
  src,
  maxWidth,
  style
}) => {
  if (isImageUrl(src)) {
    return <img src={src} alt='Preview' style={{ ...imgStyle(maxWidth), ...style }} />;
  }
  return <VideoWithFallback src={src} maxWidth={maxWidth} style={style} />;
};

// Muestra video con fallback en caso de error
const VideoWithFallback: React.FC<{ src: string; maxWidth: number; style?: React.CSSProperties }> = ({
  src,
  maxWidth,
  style
}) => {
  const [hasError, setHasError] = React.useState(false);

  // Resetear error cuando cambia la URL
  React.useEffect(() => {
    setHasError(false);
  }, [src]);

  if (hasError) {
    return <NoPreviewFallback maxWidth={maxWidth} />;
  }

  return (
    <video
      src={src}
      controls
      autoPlay
      muted
      loop
      onError={() => setHasError(true)}
      style={{ width: '100%', maxWidth, height: 'auto', borderRadius: 8, outline: 'none', ...style }}
    />
  );
};

// Fallback cuando no hay preview disponible
const NoPreviewFallback: React.FC<{ maxWidth: number }> = ({ maxWidth }) => {
  const { screens, fontSize } = useResponsiveSizes();

  return (
    <div
      style={{
        width: '100%',
        maxWidth,
        height: screens.xs ? 200 : screens.sm ? 240 : 280,
        backgroundColor: '#eaeaea',
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#a0a0a0',
        fontSize: fontSize.base
      }}
    >
      Sin preview disponible
    </div>
  );
};

// Componente principal para renderizar previews
export const PreviewRenderer: React.FC<PreviewRendererProps> = ({ previews, thumbnail }) => {
  const { screens, fontSize, spacing } = useResponsiveSizes();

  const maxWidth = screens.xs ? 280 : screens.sm ? 320 : screens.md ? 380 : 420;

  // Sin previews
  if (!previews) {
    if (thumbnail) {
      return <img src={thumbnail} alt='Thumbnail' style={imgStyle(maxWidth)} />;
    }
    return <NoPreviewFallback maxWidth={maxWidth} />;
  }

  // Legacy: string URL
  if (typeof previews === 'string') {
    return <MediaElement src={previews} maxWidth={maxWidth} />;
  }

  // MediaVariant: { video?, image? }
  if (isMediaVariant(previews)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {previews.video && <MediaElement src={previews.video} maxWidth={maxWidth} />}
        {previews.image && <img src={previews.image} alt='Preview' style={imgStyle(maxWidth)} />}
      </div>
    );
  }

  // Multiple PreviewVariants
  if (Array.isArray(previews)) {
    const firstVariant = previews[0];
    return (
      <div>
        {renderSingleVariant(firstVariant, maxWidth)}
        {previews.length > 1 && (
          <div style={{ textAlign: 'center', marginTop: spacing.sm, color: '#6b7280', fontSize: fontSize.sm }}>
            Motivo {firstVariant.metadata?.variant} de {previews.length}
          </div>
        )}
      </div>
    );
  }

  return renderSingleVariant(previews, maxWidth);
};

const renderSingleVariant = (variant: PreviewVariant, maxWidth: number) => (
  <>
    {variant.video && <MediaElement src={variant.video} maxWidth={maxWidth} style={{ marginBottom: 8 }} />}
    {variant.image && <img src={variant.image} alt='Preview' style={imgStyle(maxWidth)} />}
  </>
);
